import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  MICROSOFT_GRAPH_EMAIL,
  useFeatureEnabled,
} from "@/context/FeatureFlagsContext";
import { api } from "@/services/api";
import type { MyGraphMailboxStatus } from "@/types/integration";

export type GraphBannerVariant =
  | "connect"
  | "reauthorize"
  | "connected"
  | "wrong_account";

export interface GraphMailboxBannerState {
  variant: GraphBannerVariant;
  mailbox: MyGraphMailboxStatus | null;
  /** Extra context (e.g. Microsoft error detail) shown under the copy. */
  detail: string | null;
  authorizing: boolean;
  /**
   * Whether the user may close the banner. The onboarding prompts
   * (connect / re-authorize / wrong account) are NOT dismissible — they clear
   * only once the mailbox is authorized. Only the one-shot success message can
   * be dismissed.
   */
  dismissible: boolean;
  onAuthorize: () => Promise<void>;
  onDismiss: () => void;
}

/**
 * Drives the app-wide Microsoft Graph onboarding banner. Resolves the logged-in
 * user's own mailbox (via email match) and surfaces a connect / re-authorize
 * prompt, plus one-shot success / wrong-account messages coming back from the
 * OAuth callback via URL params (`graph_connected`).
 *
 * The onboarding banner is intentionally non-dismissible: it disappears only
 * once the user has authorized Outlook (mailbox no longer needs authorization).
 */
export function useGraphMailboxBanner(): GraphMailboxBannerState | null {
  const enabled = useFeatureEnabled(MICROSOFT_GRAPH_EMAIL);
  const [searchParams, setSearchParams] = useSearchParams();

  const [mailbox, setMailbox] = useState<MyGraphMailboxStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [authorizing, setAuthorizing] = useState(false);

  // One-shot banner coming back from the OAuth callback redirect.
  const [transient, setTransient] = useState<{
    variant: GraphBannerVariant;
    detail: string | null;
  } | null>(null);

  useEffect(() => {
    const graphConnected = searchParams.get("graph_connected");
    if (graphConnected == null) return;

    if (graphConnected === "1") {
      setTransient({ variant: "connected", detail: null });
    } else if (graphConnected === "0") {
      const reason = searchParams.get("reason");
      const detail = searchParams.get("detail");
      setTransient({
        variant: reason === "wrong_account" ? "wrong_account" : "reauthorize",
        detail,
      });
    }

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("graph_connected");
        params.delete("reason");
        params.delete("detail");
        return params;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .getMyGraphMailbox()
      .then((res) => {
        if (!cancelled) setMailbox(res.mailbox);
      })
      .catch(() => {
        if (!cancelled) setMailbox(null);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const onAuthorize = useCallback(async () => {
    if (!mailbox) return;
    setAuthorizing(true);
    try {
      const { authorize_url } = await api.getGraphAuthorizeUrl(
        mailbox.mailbox_id,
      );
      window.location.href = authorize_url;
    } catch {
      setAuthorizing(false);
    }
  }, [mailbox]);

  if (!enabled) return null;

  // Transient callback messages take precedence over the standing prompt. Only
  // the success message can be dismissed; the wrong-account prompt stays until
  // the user retries.
  if (transient) {
    return {
      variant: transient.variant,
      mailbox,
      detail: transient.detail,
      authorizing,
      dismissible: transient.variant === "connected",
      onAuthorize,
      onDismiss: () => setTransient(null),
    };
  }

  if (!loaded || !mailbox || !mailbox.needs_authorization) return null;

  const variant: GraphBannerVariant =
    mailbox.status === "pending_consent" ? "connect" : "reauthorize";

  return {
    variant,
    mailbox,
    detail: variant === "reauthorize" ? mailbox.last_error : null,
    authorizing,
    dismissible: false,
    onAuthorize,
    onDismiss: () => {},
  };
}
